// CareLogix™ ORACLE — ORACLE EVENT INTELLIGENCE™ (Phase 32A)
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PALETTE, scoreColor } from "@/components/animation/MotionShared";
import { eventApi } from "@/api/eventClient";

const DOMAIN_OPTIONS = [
  { id: "",             label: "All" },
  { id: "economic",     label: "Economic" },
  { id: "healthcare",   label: "Healthcare" },
  { id: "geopolitical", label: "Geopolitical" },
  { id: "energy",       label: "Energy" },
  { id: "sports",       label: "Sports" },
];

function ScenarioRow({ scenario, testid }) {
  const c = scoreColor(scenario.confidence);
  const w = Math.max(2, Math.min(100, Number(scenario.probability) || 0));
  return (
    <li style={S.scenario} data-testid={testid}>
      <div style={S.scenarioHead}>
        <span style={S.scenarioLbl}>{scenario.label}</span>
        <span style={{ ...S.scenarioPct, color: c }}>{scenario.probability}%</span>
      </div>
      <div style={S.scenarioBar}>
        <motion.div
          style={{ ...S.scenarioBarFill, background: c }}
          initial={{ width: "0%" }}
          animate={{ width: `${w}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
      </div>
      <p style={S.scenarioImpact}>{scenario.market_impact}</p>
      <p style={S.scenarioConf}>Confidence: {scenario.confidence}</p>
    </li>
  );
}

function EventCard({ e }) {
  const impactColor = scoreColor(e.expected_impact_score);
  return (
    <motion.article
      style={S.card}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid={`p32-event-card-${e.event_id}`}
    >
      <header style={S.cardHead}>
        <div style={S.cardHeadLeft}>
          <span style={{ ...S.icon, color: PALETTE.gold }}>{e.icon}</span>
          <div>
            <span style={S.eventTitle} data-testid={`p32-event-card-${e.event_id}-title`}>{e.title}</span>
            <div style={S.metaRow}>
              <span style={S.issuer}>{e.issuer}</span>
              {e.region && <span style={S.region}>· {e.region}</span>}
              <span style={S.days}>· ~{e.days_to_event}d</span>
              <span style={{ ...S.domainPill, color: impactColor, borderColor: impactColor }}>{e.domain_label}</span>
            </div>
          </div>
        </div>
        <div style={S.scoreRing} data-testid={`p32-event-card-${e.event_id}-impact`}>
          <span style={{ ...S.scoreVal, color: impactColor }}>{e.expected_impact_score}</span>
          <span style={S.scoreLbl}>Impact</span>
        </div>
      </header>

      {Array.isArray(e.asset_impact) && e.asset_impact.length > 0 && (
        <div style={S.assetRow} data-testid={`p32-event-card-${e.event_id}-assets`}>
          <span style={S.assetLbl}>Assets:</span>
          {e.asset_impact.map((a, i) => (
            <span key={i} style={S.assetPill}>{a}</span>
          ))}
        </div>
      )}

      <ul style={S.scenarios} data-testid={`p32-event-card-${e.event_id}-scenarios`}>
        {(e.scenarios || []).map((sc, i) => (
          <ScenarioRow scenario={sc} key={i} testid={`p32-event-scenario-${e.event_id}-${i}`} />
        ))}
      </ul>

      <div style={S.metaGrid}>
        <div style={S.metaCell}>
          <span style={S.metaLbl}>Confidence</span>
          <span style={{ ...S.metaVal, color: scoreColor(e.confidence_score) }}>{e.confidence_score}</span>
        </div>
        <div style={S.metaCell}>
          <span style={S.metaLbl}>Uncertainty</span>
          <span style={{ ...S.metaVal, color: PALETTE.amber }}>{e.uncertainty_score}%</span>
        </div>
      </div>

      {e.compliance_block && (
        <div style={S.complianceBlock} data-testid={`p32-event-card-${e.event_id}-compliance-block`}>
          ⚠ {e.compliance_block}
        </div>
      )}

      <footer style={S.cardFoot}>
        <span style={S.cardCompliance}>Research only · Not financial advice · No guarantees · Human review required.</span>
      </footer>
    </motion.article>
  );
}

export default function EventIntelligenceCenter({ userRegion, testid = "p32-event-intel" }) {
  const [domain, setDomain] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 16 };
      if (domain) params.domain = domain;
      if (userRegion) params.user_region = userRegion;
      const res = await eventApi.upcoming(params);
      setData(res.data);
    } catch (e) {
      setError("Could not load events — retrying may help.");
    } finally {
      setLoading(false);
    }
  }, [domain, userRegion]);

  useEffect(() => { load(); }, [load]);

  const events = useMemo(() => (data && Array.isArray(data.events)) ? data.events : [], [data]);

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.header}>
        <div>
          <h2 style={S.title} data-testid={`${testid}-title`}>ORACLE EVENT INTELLIGENCE™</h2>
          <p style={S.subtitle} data-testid={`${testid}-subtitle`}>
            Global events with Probability Engine™ — 5 domains, jurisdiction & responsible-gaming locks enforced.
          </p>
        </div>
        <span style={S.complianceBadge}>COMPLIANCE LOCK™</span>
      </header>

      <div style={S.universeRow} data-testid={`${testid}-domain-selector`}>
        {DOMAIN_OPTIONS.map((d) => (
          <button
            key={d.id || "all"}
            type="button"
            onClick={() => setDomain(d.id)}
            style={{ ...S.uniBtn, ...(domain === d.id ? S.uniBtnActive : null) }}
            data-testid={`${testid}-domain-${d.id || "all"}`}
            aria-pressed={domain === d.id}
          >
            {d.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {loading && (
          <motion.div key="loading" style={S.loading} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid={`${testid}-loading`}>
            Loading global event radar…
          </motion.div>
        )}
        {!loading && error && (
          <motion.div key="err" style={S.err} initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid={`${testid}-error`}>
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {!loading && events.length > 0 && (
        <div style={S.cards} data-testid={`${testid}-cards`}>
          {events.map((e) => <EventCard e={e} key={e.event_id} />)}
        </div>
      )}

      <div style={S.footnote}>
        {data?.disclaimer || "Research only. Not financial advice. Past performance not predictive."}
      </div>
    </section>
  );
}

const S = {
  shell:        { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 18, padding: 18, display: "flex", flexDirection: "column", gap: 14, color: PALETTE.slate3 },
  header:       { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" },
  title:        { margin: 0, color: PALETTE.goldBright, fontSize: 18, letterSpacing: "0.07em" },
  subtitle:     { margin: "4px 0 0 0", color: PALETTE.slate4, fontSize: 12.5, lineHeight: 1.5 },
  complianceBadge: { color: PALETTE.amber, border: `1px solid ${PALETTE.amber}`, padding: "3px 9px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.12em", background: "rgba(252,211,77,0.06)" },
  universeRow:  { display: "flex", flexWrap: "wrap", gap: 6 },
  uniBtn:       { appearance: "none", background: "transparent", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.slate3, padding: "6px 11px", borderRadius: 999, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11.5, letterSpacing: "0.06em" },
  uniBtnActive: { color: PALETTE.bgDeep, background: PALETTE.gold, borderColor: PALETTE.gold, fontWeight: 800 },
  cards:        { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 12 },
  card:         { background: "linear-gradient(135deg, rgba(11,15,20,0.95), rgba(8,32,22,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 14, padding: 12, display: "flex", flexDirection: "column", gap: 10 },
  cardHead:     { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 },
  cardHeadLeft: { display: "flex", alignItems: "flex-start", gap: 10 },
  icon:         { fontSize: 24, lineHeight: 1, width: 28 },
  eventTitle:   { color: PALETTE.goldBright, fontSize: 14, fontWeight: 700, letterSpacing: "0.03em" },
  metaRow:      { display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4, alignItems: "center" },
  issuer:       { color: PALETTE.slate3, fontSize: 11 },
  region:       { color: PALETTE.slate4, fontSize: 11 },
  days:         { color: PALETTE.slate4, fontSize: 11 },
  domainPill:   { fontFamily: "'Space Mono', monospace", fontSize: 9, padding: "2px 6px", borderRadius: 999, border: "1px solid", letterSpacing: "0.10em", textTransform: "uppercase" },
  scoreRing:    { display: "flex", flexDirection: "column", alignItems: "center", padding: "6px 10px", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 12, background: "rgba(197,160,89,0.05)", minWidth: 64 },
  scoreVal:     { fontFamily: "'Space Mono', monospace", fontSize: 18, fontWeight: 800 },
  scoreLbl:     { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.10em" },
  assetRow:     { display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" },
  assetLbl:     { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.10em" },
  assetPill:    { color: PALETTE.slate3, border: "1px solid rgba(255,255,255,0.08)", padding: "2px 8px", borderRadius: 999, fontSize: 10.5, background: "rgba(255,255,255,0.02)" },
  scenarios:    { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 },
  scenario:     { background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8, padding: 8 },
  scenarioHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  scenarioLbl:  { color: PALETTE.slate3, fontSize: 12, fontWeight: 600 },
  scenarioPct:  { fontFamily: "'Space Mono', monospace", fontSize: 12.5, fontWeight: 800 },
  scenarioBar:  { marginTop: 5, height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" },
  scenarioBarFill: { height: "100%", width: 0 },
  scenarioImpact:  { margin: "6px 0 0 0", color: PALETTE.slate4, fontSize: 11.5, lineHeight: 1.4 },
  scenarioConf:    { margin: "4px 0 0 0", color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.06em" },
  metaGrid:     { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 6 },
  metaCell:     { background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8, padding: "6px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  metaLbl:      { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.10em" },
  metaVal:      { fontFamily: "'Space Mono', monospace", fontSize: 12.5, fontWeight: 800 },
  complianceBlock: { background: "rgba(252,211,77,0.06)", border: `1px solid ${PALETTE.amber}`, color: PALETTE.amber, padding: "6px 10px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.4 },
  cardFoot:     { display: "flex", justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap" },
  cardCompliance:{ color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5 },
  loading:      { color: PALETTE.slate4, fontSize: 13, padding: "20px 0" },
  err:          { color: PALETTE.red, fontSize: 13, padding: "20px 0" },
  footnote:     { color: PALETTE.slate5, fontSize: 10.5, fontStyle: "italic", textAlign: "right" },
};
